export const availabilityLabels: Record<string, string> = {
  available_automatically: "Available automatically",
  available_approved: "Available—admin approved",
  awaiting_approval: "Awaiting approval",
  needs_correction: "Needs correction",
  rejected: "Rejected",
  outdated: "Outdated",
};
export const availabilityDescriptions: Record<string, string> = {
  available_automatically:
    "This predicted answer passed automated checks and can be reused for a matching question during diagnosis. Conversation and freshness checks still apply. If it cannot be reused, the live pipeline generates an answer.",
  available_approved:
    "An administrator approved this verified answer. It can be reused for a matching question during diagnosis, subject to conversation and freshness checks. If it cannot be reused, the live pipeline generates an answer.",
  awaiting_approval:
    "This answer passed verification but is not available for reuse yet. It needs administrator approval, publication, or resolution of a competing answer. Diagnosis uses the live pipeline in the meantime.",
  needs_correction:
    "This answer is not available for reuse because verification is unfinished or a check did not pass. Select it to see the specific reason. Diagnosis can still answer the question through the live pipeline.",
  rejected:
    "An administrator rejected this answer, so it will not be reused. The decision is retained in review history. Diagnosis can still answer the question through the live pipeline.",
  outdated:
    "The case or its instructions changed, or a newer generation replaced this answer. This version will not be reused. Diagnosis uses another available answer or the live pipeline.",
};
export const checkLabels: Record<string, string> = {
  factSupported: "Supported by the case",
  noConflict: "Consistent evidence",
  patientKnows: "Patient knowledge",
  disclosureSafe: "Disclosure rules",
  contextIndependent: "Works without prior conversation",
  answerScope: "Answers the whole question",
  patientVoice: "Patient voice",
  wording: "Answer wording",
  personalitySafe: "Patient behavior",
  singleFact: "Answer scope",
  templateSafe: "Answer wording",
};
export const subjectiveChecks = ["patientVoice", "wording", "personalitySafe"];
export function reviewActionExplanation(
  source: string,
  action: string,
): string {
  if (action === "reject")
    return "This answer will no longer be reused. The decision stays in its review history.";
  if (action === "approve")
    return "Publishes the unchanged, verified answer. Save and check edited answers first.";
  return source === "session"
    ? "Saves your edits and checks them in the background. Administrator approval is still needed before the edited answer is reused."
    : "Saves your edits and checks them in the background. Answers that pass become available automatically.";
}

// Match the normalization of verified question examples used by the backend.
export const reviewQuestionKey = (question: string): string =>
  question
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[’]/g, "'")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[?.!]+$/, "")
    .trim();

// Preserve the exact stored wording while deduplicating harmless formatting.
export function uniqueReviewQuestions(questions: string[]): string[] {
  const unique = new Map<string, string>();
  for (const question of questions) {
    const key = reviewQuestionKey(question);
    if (key && !unique.has(key)) unique.set(key, question);
  }
  return [...unique.values()];
}

export function mergeReviewQuestions(
  existing: string[],
  incoming: string[],
  selected: string[],
): string[] {
  const allowed = new Set(incoming.map(reviewQuestionKey));
  if (
    !selected.length ||
    selected.some((q) => !allowed.has(reviewQuestionKey(q)))
  )
    throw Error(
      "Select at least one question from the answer you’re reviewing.",
    );
  const merged = uniqueReviewQuestions([...existing, ...selected]);
  if (merged.length > 80)
    throw Error(
      "An answer can have at most 80 question phrasings. Select fewer questions.",
    );
  return merged;
}

interface ReviewStatusAnswer {
  grouping?: {
    proposals: Array<{
      relation: string;
      mergeCheck?: { status: string; reason: string };
    }>;
  } | null;
  availability: string;
  source: string;
  processingStatus?: string;
  verificationState: string;
  published: unknown | null;
  checks?: Record<string, { status: string; needsEvidenceReview?: boolean }>;
  draft: {
    verificationState: string;
    checks?: Record<string, { status: string; needsEvidenceReview?: boolean }>;
  } | null;
}
export function answerReviewStatus(answer: ReviewStatusAnswer): {
  label: string;
  color: "success" | "warning" | "info" | "error" | "default";
  attention: "approval" | "checking" | "correction" | "publication" | null;
  description: string;
} {
  const { availability, processingStatus, draft, source } = answer;
  const previousAvailable =
    !!answer.published && availability.startsWith("available");
  const changes = previousAvailable && !!draft;
  const prior = changes
    ? " The published answer remains available while these changes are reviewed."
    : "";
  const result = (
    label: string,
    color: "warning" | "info" | "error",
    attention: "approval" | "checking" | "correction" | "publication",
    description: string,
  ) => ({ label, color, attention, description: description + prior });
  if (availability === "rejected" || availability === "outdated")
    return {
      label: availabilityLabels[availability],
      color: "default",
      attention: null,
      description: availabilityDescriptions[availability],
    };
  if (processingStatus === "pending" || processingStatus === "processing")
    return result(
      changes ? "Checking changes" : "Checking answer",
      "info",
      "checking",
      "Verification is running in the background. Approval is unavailable until checking finishes.",
    );
  if (processingStatus === "failed")
    return result(
      changes ? "Changes need checking" : "Checking failed",
      "error",
      "correction",
      "Background checking failed. Open this question to review the error and retry.",
    );
  const state = draft?.verificationState || answer.verificationState;
  const checks = draft?.checks || answer.checks;
  if (
    state !== "passed" &&
    checks?.factSupported?.needsEvidenceReview &&
    !Object.entries(checks).some(
      ([key, check]) => key !== "factSupported" && check.status !== "passed",
    )
  )
    return result(
      changes ? "Changes need evidence review" : "Needs evidence review",
      "warning",
      "correction",
      "Verification could not confirm the answer's support. Review the evidence or explain the supported interpretation and recheck.",
    );
  if (changes || availability === "awaiting_approval") {
    const blockedMerge =
      !answer.published &&
      state === "passed" &&
      answer.grouping?.proposals.find(
        (proposal) =>
          proposal.relation === "equivalent" &&
          proposal.mergeCheck?.status === "blocked",
      );
    if (blockedMerge)
      return result(
        "Automatic merge blocked",
        "warning",
        "correction",
        `The answers were judged equivalent, but automatic merging was blocked. ${blockedMerge.mergeCheck!.reason}`,
      );
    if (state === "passed")
      return source === "session"
        ? result(
            changes ? "Changes need approval" : "Needs approval",
            "warning",
            "approval",
            "Verification passed. Administrator approval is required before this answer or its changes can be reused.",
          )
        : result(
            changes ? "Changes awaiting publication" : "Awaiting publication",
            "warning",
            "publication",
            "Verification passed. This answer or its changes still await publication or resolution of a competing answer.",
          );
    if (state === "pending")
      return result(
        changes ? "Changes need checking" : "Needs checking",
        "info",
        "checking",
        "These changes have not finished verification. Open the question to check progress or retry.",
      );
    return result(
      changes ? "Changes need correction" : "Needs correction",
      "error",
      "correction",
      "A verification check did not pass. Open the question to review the issues.",
    );
  }
  if (availability === "needs_correction")
    return state === "pending"
      ? result(
          "Needs checking",
          "info",
          "checking",
          "Verification has not finished. Open the question to check progress or retry.",
        )
      : result(
          "Needs correction",
          "error",
          "correction",
          "A verification check did not pass. Open the question to review the issues.",
        );
  return {
    label: availabilityLabels[availability] || availability,
    color: previousAvailable ? "success" : "default",
    attention: null,
    description: availabilityDescriptions[availability] || "",
  };
}
/** The server supplies only current, unambiguous question associations. */
export function reviewAnswerGroups<
  T extends { id: string; questionGroupId?: string },
>(answers: T[]): Array<{ answer: T; members: T[] }> {
  const byId = new Map(answers.map((answer) => [answer.id, answer]));
  const groups = new Map<string, { answer: T; members: T[] }>();
  for (const answer of answers) {
    const root = byId.get(answer.questionGroupId || "") || answer;
    const group = groups.get(root.id) || { answer: root, members: [] };
    group.members.push(answer);
    groups.set(root.id, group);
  }
  return [...groups.values()];
}
interface ApprovalContent {
  answerText: string;
  questionExamples: string[];
  verificationState?: string;
}

/** Only mark recorded automatic additions that still belong to this publication. */
export function automaticallyApprovedQuestions(answer: {
  source: string;
  availability: string;
  published: ApprovalContent | null;
  reviewHistory: Array<{
    actor: string;
    action: string;
    questions?: string[];
    removedWordings?: string[];
    after?: Partial<ApprovalContent>;
  }>;
}): string[] {
  const published = answer.published;
  if (!published || !answer.availability.startsWith("available")) return [];
  const approvals = new Map<string, string>();
  for (const event of answer.reviewHistory) {
    if (event.action === "reject") approvals.clear();
    const after = event.after;
    const publicationChanged =
      ["questions_auto_merged", "approve", "merge_received"].includes(
        event.action,
      ) ||
      (event.action === "validate" &&
        answer.source === "predicted" &&
        after?.verificationState === "passed");
    if (
      publicationChanged &&
      typeof after?.answerText === "string" &&
      Array.isArray(after.questionExamples)
    ) {
      for (const [question, speech] of approvals)
        if (
          speech !== after.answerText ||
          !after.questionExamples.includes(question)
        )
          approvals.delete(question);
    }
    if (event.action === "wording_assigned")
      for (const question of event.removedWordings || [])
        approvals.delete(question);
    if (
      event.actor === "system" &&
      event.action === "questions_auto_merged" &&
      typeof after?.answerText === "string" &&
      Array.isArray(after.questionExamples)
    )
      for (const question of event.questions || [])
        if (
          typeof question === "string" &&
          after.questionExamples.includes(question)
        )
          approvals.set(question, after.answerText);
  }
  return [...new Set(published.questionExamples)].filter(
    (question) => approvals.get(question) === published.answerText,
  );
}
